import { useCallback, useRef, useState } from 'react';
import { CheckCircleIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import cn from 'classnames';

import DropdownFoldout from 'components/generic/DropdownFoldout.component';
import { MobileBottomSheet } from 'components/generic/MobileBottomSheet.component';
import useIsMobileViewport from 'hooks/useIsMobileViewport.hook';
import useTip from 'hooks/useTip.hook';
import { useTranslation } from 'providers/GILT.provider';
import { useTheme } from 'providers/Theme.provider';

import { ORDERED_TYPES, QUESTION_TYPE_LABELS } from '../consultationForms.const';

import CN from './QuestionTypeDropdown.module.scss';

export const QuestionTypeDropdown = ({ value, onChange }) => {
  const { t } = useTranslation();
  const { isDarkMode } = useTheme();
  const isMobile = useIsMobileViewport();
  const { val: isOpen, tip: toggle } = useTip(false);
  const ref = useRef(null);
  const [dropdownPosition, setDropdownPosition] = useState('bottomLeft');

  const isSelected = (type) => type === value;

  const handleSelect = (type) => {
    onChange(type);
    toggle();
  };

  const handleToggle = useCallback(() => {
    if (!isOpen && !isMobile && ref.current) {
      const rect = ref.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      setDropdownPosition(spaceBelow < 300 ? 'top' : 'bottomLeft');
    }
    toggle();
  }, [isOpen, isMobile, toggle]);

  const optionsList = (
    <div data-bem="QuestionTypeDropdown__options" className={cn(CN.optionsList, { [CN.darkMode]: isDarkMode })} role="listbox" aria-multiselectable="false">
      {ORDERED_TYPES.map((type) => (
        <div
          data-bem="QuestionTypeDropdown__option"
          data-testid={`QuestionTypeDropdown__option-${type}`}
          key={type}
          className={cn(CN.option)}
          role="option"
          aria-selected={isSelected(type)}
          tabIndex={isSelected(type) ? -1 : 0}
          aria-label={t(QUESTION_TYPE_LABELS[type])}
          onClick={() => handleSelect(type)}
        >
          <span className={CN.optionLabel}>{t(QUESTION_TYPE_LABELS[type])}</span>
          <span className={CN.radio} aria-hidden="true">
            {isSelected(type) ? <CheckCircleIcon className={CN.checkCircleIcon} /> : <div className={CN.unselectedCircle} />}
          </span>
        </div>
      ))}
    </div>
  );

  return (
    <div ref={ref} data-bem="QuestionTypeDropdown" className={cn(CN.component, { [CN.darkMode]: isDarkMode })}>
      <button
        data-bem="QuestionTypeDropdown__trigger"
        data-testid="QuestionTypeDropdown__trigger"
        className={CN.trigger}
        type="button"
        onClick={handleToggle}
        aria-label={t(QUESTION_TYPE_LABELS[value])}
        aria-expanded={isOpen}
        aria-controls={isOpen ? 'QuestionTypeDropdown__options' : undefined}
      >
        <span className={CN.triggerLabel} aria-hidden="true">
          {t(QUESTION_TYPE_LABELS[value])}
        </span>
        <ChevronDownIcon className={CN.chevronDownIcon} />
      </button>

      {isMobile ? (
        <MobileBottomSheet isOpen={isOpen} onClose={toggle}>
          {optionsList}
        </MobileBottomSheet>
      ) : (
        isOpen && (
          <DropdownFoldout width="fit" position={dropdownPosition} onClickOutside={toggle} refFromProps={ref}>
            <DropdownFoldout.Body>{optionsList}</DropdownFoldout.Body>
          </DropdownFoldout>
        )
      )}
    </div>
  );
};
